import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import './OnCallReportGroupSummary.css';
import LoadingComp from '../common/loading.js';
import { ApiError } from '../common/apiError.js';
import { SEC_ApiCall } from '../func.js';
import { PieChartComponent } from './chartComponents/PieChartComponent';
import {
    buildOnCallPupilSummaryRows,
    buildOnCallPupilGroupLabels,
    downloadCsvFromRows,
    filterOnCallReportPupilRecords,
    formatOnCallExportCreatedDate,
    formatOnCallExportRangeDate,
    getOnCallReportCallTypeOptions,
} from './OnCallFunc.js';
import { LessonTimesCountTable } from './tableComponents/LessonTimesCountTable.js';
import { WeeklyCallTrendsLineChart } from './chartComponents/WeeklyCallTrendsLineChart.js';
import { PupilCallTypeSummaryTable } from './tableComponents/PupilCallTypeSummaryTable.js';
import OnCallReportDateFilter from './OnCallReportDateFilter.js';
import OnCallReportFiltersSidebar from './OnCallReportFiltersSidebar.js';

const DEFAULT_PUPIL_GROUP_FILTERS = {
    FSM: 'all',
    ALN: 'all',
    MTT: 'all',
};

function formatDateForInput(dateValue) {
    return dateValue.toISOString().split('T')[0];
}


function getDefaultAcademicYearDates() {
    const today = new Date();
    const currentYear = today.getFullYear();
    const septemberFirstThisYear = new Date(currentYear, 8, 1);
    const startDate = today >= septemberFirstThisYear
        ? septemberFirstThisYear
        : new Date(currentYear - 1, 8, 1);

    return {
        startDate: formatDateForInput(startDate),
        endDate: formatDateForInput(today),
    };
}

function normaliseReportResponse(response) {
    if (typeof response === 'string') {
        try {
            return JSON.parse(response);
        } catch (error) {
            console.error('Failed to parse whole-school report payload string', error);
            return {};
        }
    }

    if (response && typeof response?.body === 'string') {
        try {
            return JSON.parse(response.body);
        } catch (error) {
            console.error('Failed to parse whole-school report response body', error);
            return {};
        }
    }

    return response && typeof response === 'object' ? response : {};
}

export default function OnCallReportWholeSchool() {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const [dataState, setDataState] = useState(null);
    const [filteredDataState, setFilteredDataState] = useState(null);
    const [selectedCallType, setSelectedCallType] = useState(null);
    const [isLoadedState, setIsLoadedState] = useState(false);
    const [isApplyingDateFilterState, setIsApplyingDateFilterState] = useState(false);
    const [apiErrorState, setApiErrorState] = useState(false);
    const [allCallsArrayState, setAllCallsArrayState] = useState([]);
    const [totalCallCountState, setTotalCallCountState] = useState(0);
    const [selectedCallTypesState, setSelectedCallTypesState] = useState([]);
    const [selectedLessonsState, setSelectedLessonsState] = useState([]);
    const [selectedPupilGroupFiltersState, setSelectedPupilGroupFiltersState] = useState(DEFAULT_PUPIL_GROUP_FILTERS);
    const [selectedTermRangeState, setSelectedTermRangeState] = useState(null);
    const defaultDates = getDefaultAcademicYearDates();
    const [startDateState, setStartDateState] = useState(searchParams.get('startDate') || defaultDates.startDate);
    const [endDateState, setEndDateState] = useState(searchParams.get('endDate') || defaultDates.endDate);

    const callTypeOptions = useMemo(() => {
        const allRecords = (dataState?.pupilRecords || []).flatMap((pupilRecord) => pupilRecord?.onCallRecords || []);
        return getOnCallReportCallTypeOptions(dataState?.onCallType || [], allRecords);
    }, [dataState]);

    const lessonOptions = useMemo(() => {
        return Array.isArray(dataState?.timetableTimings)
            ? dataState.timetableTimings.map((timing) => timing?.period).filter(Boolean)
            : [];
    }, [dataState]);

    function applyFilteredPupils(filteredPupils, sourceData) {
        const allFilteredRecords = filteredPupils.flatMap((pupilRecord) => pupilRecord.onCallRecords || []);

        setFilteredDataState({
            ...sourceData,
            pupilRecords: filteredPupils,
        });
        setAllCallsArrayState(allFilteredRecords);
        setTotalCallCountState(allFilteredRecords.length);
    }

    function resetFilters(sourceData = dataState) {
        const allRecords = (sourceData?.pupilRecords || []).flatMap((pupilRecord) => pupilRecord.onCallRecords || []);
        const allCallTypeNames = getOnCallReportCallTypeOptions(sourceData?.onCallType || [], allRecords)
            .map((callType) => callType.typeName);
        const allLessonNames = Array.isArray(sourceData?.timetableTimings)
            ? sourceData.timetableTimings.map((timing) => timing?.period).filter(Boolean)
            : [];

        setSelectedCallType(null);
        setSelectedCallTypesState(allCallTypeNames);
        setSelectedLessonsState(allLessonNames);
        setSelectedPupilGroupFiltersState(DEFAULT_PUPIL_GROUP_FILTERS);
        setFilteredDataState(sourceData);
        setAllCallsArrayState(allRecords);
        setTotalCallCountState(allRecords.length);
    }

    function handleLessonSelect(selectedPeriod) {
        if (!dataState) {
            return;
        }

        setSelectedLessonsState((previousLessons) => {
            if (previousLessons.includes(selectedPeriod)) {
                return previousLessons.filter((lessonName) => lessonName !== selectedPeriod);
            }

            const lessonSet = new Set(previousLessons);
            lessonSet.add(selectedPeriod);

            return lessonOptions.filter((lessonName) => lessonSet.has(lessonName));
        });
    }

    function handleCallTypeSelect(selectedType) {
        if (!dataState) {
            return;
        }

        if (selectedType === selectedCallType) {
            setSelectedCallType(null);
            return;
        }

        setSelectedCallType(selectedType);
    }

    function handleSidebarCallTypeToggle(callTypeName) {
        setSelectedCallTypesState((previousSelectedTypes) => {
            if (previousSelectedTypes.includes(callTypeName)) {
                return previousSelectedTypes.filter((selectedType) => selectedType !== callTypeName);
            }

            const selectedTypeSet = new Set(previousSelectedTypes);
            selectedTypeSet.add(callTypeName);

            return callTypeOptions
                .map((callType) => callType.typeName)
                .filter((typeName) => selectedTypeSet.has(typeName));
        });

        if (selectedCallType === callTypeName) {
            setSelectedCallType(null);
        }
    }

    function handleSidebarLessonToggle(lessonName) {
        setSelectedLessonsState((previousLessons) => {
            if (previousLessons.includes(lessonName)) {
                return previousLessons.filter((selectedLessonName) => selectedLessonName !== lessonName);
            }

            const lessonSet = new Set(previousLessons);
            lessonSet.add(lessonName);

            return lessonOptions.filter((periodName) => lessonSet.has(periodName));
        });
    }

    function handlePupilGroupFilterChange(groupName, selectedValue) {
        setSelectedPupilGroupFiltersState((previousFilters) => ({
            ...previousFilters,
            [groupName]: selectedValue,
        }));
    }

    async function apiCall(params, isRefresh = false) {
        if (isRefresh) {
            setIsApplyingDateFilterState(true);
        }

        try {
            setApiErrorState(false);
            const response = await SEC_ApiCall('oncall/reports/wholeSchool', params, setApiErrorState);
            const normalisedResponse = normaliseReportResponse(response);
            const pupilRecords = Array.isArray(normalisedResponse?.pupilRecords) ? normalisedResponse.pupilRecords : [];
            const allRecords = pupilRecords.flatMap((pupilRecord) => pupilRecord.onCallRecords || []);

            const cleanResponse = {
                ...normalisedResponse,
                pupilRecords,
                onCallType: Array.isArray(normalisedResponse?.onCallType) ? normalisedResponse.onCallType : [],
                timetableTimings: normalisedResponse?.timetableTimings || {},
                termDateRanges: normalisedResponse?.termDateRanges || {},
            };
            const availableCallTypes = getOnCallReportCallTypeOptions(cleanResponse.onCallType, allRecords)
                .map((callType) => callType.typeName);
            const availableLessons = Array.isArray(cleanResponse?.timetableTimings)
                ? cleanResponse.timetableTimings.map((timing) => timing?.period).filter(Boolean)
                : [];

            setDataState(cleanResponse);
            setFilteredDataState(cleanResponse);
            setAllCallsArrayState(allRecords);
            setTotalCallCountState(allRecords.length);
            setSelectedCallTypesState(availableCallTypes);
            setSelectedLessonsState(availableLessons);
            setSelectedCallType(null);
            setSelectedPupilGroupFiltersState(DEFAULT_PUPIL_GROUP_FILTERS);
        } catch (error) {
            console.error('Failed to load whole-school OnCall report', error);
            setApiErrorState(true);
        } finally {
            setIsLoadedState(true);
            setIsApplyingDateFilterState(false);
        }
    }

    useEffect(() => {
        apiCall({
            startDate: startDateState,
            endDate: endDateState,
        });
    }, []);

    useEffect(() => {
        if (!dataState) {
            return;
        }

        const filteredPupils = filterOnCallReportPupilRecords(
            dataState?.pupilRecords || [],
            {
                allowedCallTypes: selectedCallTypesState,
                allowedLessons: selectedLessonsState,
                selectedCallType,
                pupilGroups: selectedPupilGroupFiltersState,
            },
            dataState?.timetableTimings || []
        );

        applyFilteredPupils(filteredPupils, dataState);
    }, [dataState, selectedCallTypesState, selectedLessonsState, selectedCallType, selectedPupilGroupFiltersState]);

    const hasActiveFilters = useMemo(() => {
        const allCallTypeNames = callTypeOptions.map((callType) => callType.typeName);
        const allLessonNames = lessonOptions;
        const hasCallTypeFilter = selectedCallTypesState.length !== allCallTypeNames.length;
        const hasLessonFilter = selectedLessonsState.length !== allLessonNames.length;
        const hasPupilGroupFilter = Object.values(selectedPupilGroupFiltersState).some((selectedValue) => selectedValue !== 'all');

        return Boolean(hasCallTypeFilter || hasLessonFilter || hasPupilGroupFilter || selectedCallType);
    }, [callTypeOptions, lessonOptions, selectedCallTypesState, selectedLessonsState, selectedPupilGroupFiltersState, selectedCallType]);

    function exportPupilsTableToCsv() {
        const effectiveCallTypeNames = selectedCallType ? [selectedCallType] : selectedCallTypesState;
        const includedCallTypesLabel = effectiveCallTypeNames.length > 0 ? effectiveCallTypeNames.join(', ') : 'None';
        const includedLessonsLabel = selectedLessonsState.length > 0 ? selectedLessonsState.join(', ') : 'None';
        const includedGroupsLabel = Object.entries(selectedPupilGroupFiltersState)
            .map(([groupName, selectedValue]) => `${groupName}: ${selectedValue === 'all' ? 'All Learners' : selectedValue}`)
            .join('; ');
        const metadataLine = `${formatOnCallExportCreatedDate(new Date())} - Whole School - Dates: ${formatOnCallExportRangeDate(startDateState)} to ${formatOnCallExportRangeDate(endDateState)} - Filters Included: Call type; ${includedCallTypesLabel} Lesson; ${includedLessonsLabel} Groups; ${includedGroupsLabel}`;
        const summaryRows = buildOnCallPupilSummaryRows(filteredDataState?.pupilRecords || [], callTypeOptions);
        const headerRow = [
            "Learner's Name",
            'Groups',
            ...callTypeOptions.map((callType) => callType.typeName),
            'Total Calls',
        ];
        const dataRows = summaryRows.map((row) => ([
            row.pupilName,
            buildOnCallPupilGroupLabels(row).join(' '),
            ...callTypeOptions.map((callType) => row.totals?.[callType.typeName] ?? 0),
            row.totals?.total ?? 0,
        ]));

        downloadCsvFromRows('oncall-whole-school-learners-list.csv', [
            [metadataLine],
            [],
            headerRow,
            ...dataRows,
        ]);
    }

    function applyDateFilter() {
        setSearchParams({
            startDate: startDateState,
            endDate: endDateState,
        });
        apiCall({
            startDate: startDateState,
            endDate: endDateState,
        }, true);
    }

    function handleTermRangeChange(selectedRange) {
        setSelectedTermRangeState(selectedRange);

        if (!selectedRange) {
            setStartDateState(defaultDates.startDate);
            setEndDateState(defaultDates.endDate);
            setSearchParams({
                startDate: defaultDates.startDate,
                endDate: defaultDates.endDate,
            });
            apiCall({
                startDate: defaultDates.startDate,
                endDate: defaultDates.endDate,
            }, true);
            return;
        }

        setStartDateState(selectedRange.startDate);
        setEndDateState(selectedRange.endDate);
        setSearchParams({
            startDate: selectedRange.startDate,
            endDate: selectedRange.endDate,
        });
        apiCall({
            startDate: selectedRange.startDate,
            endDate: selectedRange.endDate,
        }, true);
    }

    if (!isLoadedState) {
        return <LoadingComp />;
    }

    if (apiErrorState || !dataState) {
        return <ApiError />;
    }

    return (
        <div id="main_div" className='main_div'>
            <div className='oncall_reports_groupSummary_headerRow'>
                <div className='oncall_reports_groupSummary_headerText'>
                    <h1 className='oncall_reports_groupSummary_mainHeader'>Whole School Report</h1>
                    <h1 className='oncall_reports_groupSummary_subHeader'>Total Calls: {totalCallCountState}</h1>
                </div>
                <button
                    type='button'
                    className='oncall_reports_groupSummary_backButton'
                    onClick={() => navigate('/app/oncall/reports')}
                >
                    Back to Reports
                </button>
            </div>

            <OnCallReportDateFilter
                startDate={startDateState}
                endDate={endDateState}
                setStartDate={(value) => {
                    setSelectedTermRangeState(null);
                    setStartDateState(value);
                }}
                setEndDate={(value) => {
                    setSelectedTermRangeState(null);
                    setEndDateState(value);
                }}
                onApply={applyDateFilter}
                isLoading={isApplyingDateFilterState}
                termDateRanges={dataState?.termDateRanges}
                selectedTermRange={selectedTermRangeState}
                onTermRangeChange={handleTermRangeChange}
            />

            <div className='oncall_reports_groupSummary_contentLayout'>
                <div className='oncall_reports_groupSummary_contentMain'>
                    <div className='oncall_reports_groupSummary_summaryDiv'>
                        <div className='oncall_reports_groupSummary_pieChartDiv'>
                            <h1 className='oncall_reports_groupSummary_divHeader'>Call Types</h1>
                            <h1 className='oncall_reports_groupSummary_divSubHeader'>Click to filter</h1>
                            <PieChartComponent
                                callsArray={allCallsArrayState}
                                callTypeColours={callTypeOptions}
                                onCallTypeSelect={handleCallTypeSelect}
                            />
                        </div>

                        <div className='oncall_reports_groupSummary_tableDiv'>
                            <h1 className='oncall_reports_groupSummary_divHeader'>Lesson Times</h1>
                            <h1 className='oncall_reports_groupSummary_divSubHeader'>Based on call submission time</h1>
                            <h1 className='oncall_reports_groupSummary_divSubHeader'>Click lessons to add or remove them from the filter</h1>
                            <LessonTimesCountTable
                                data={allCallsArrayState}
                                timetableTimings={dataState.timetableTimings}
                                onLessonSelect={handleLessonSelect}
                                selectedLessons={selectedLessonsState}
                            />
                        </div>
                    </div>

                    <div className='oncall_reports_groupSummary_lineChartDiv'>
                        <h1 className='oncall_reports_groupSummary_divHeader'>Weekly Call Trends</h1>
                        <h1 className='oncall_reports_groupSummary_divSubHeader'>Date is the Monday of that week</h1>
                        <div className='oncall_reports_groupSummary_lineChartInnerDiv'>
                            <WeeklyCallTrendsLineChart data={allCallsArrayState} />
                        </div>
                    </div>

                    <div className='oncall_reports_groupSummary_pupilsTableDiv'>
                        <div className='oncall_reports_groupSummary_sectionHeaderRow'>
                            <h1 className='oncall_reports_groupSummary_divHeader'>Learners List</h1>
                            <button
                                type='button'
                                className='oncall_reports_groupSummary_exportButton'
                                onClick={exportPupilsTableToCsv}
                            >
                                Export CSV
                            </button>
                        </div>
                        <h1 className='oncall_reports_groupSummary_divSubHeader'>Click a column heading to sort</h1>
                        <h1 className='oncall_reports_groupSummary_divSubHeader'>Select a learner to see their individual report</h1>
                        <PupilCallTypeSummaryTable
                            pupilRecords={filteredDataState?.pupilRecords || []}
                            callTypes={callTypeOptions}
                        />
                    </div>
                </div>

                <OnCallReportFiltersSidebar
                    callTypeOptions={callTypeOptions}
                    lessonOptions={lessonOptions}
                    selectedCallTypes={selectedCallTypesState}
                    selectedLessons={selectedLessonsState}
                    pupilGroupFilters={selectedPupilGroupFiltersState}
                    onToggleCallType={handleSidebarCallTypeToggle}
                    onToggleLesson={handleSidebarLessonToggle}
                    onPupilGroupFilterChange={handlePupilGroupFilterChange}
                    onResetFilters={() => resetFilters(dataState)}
                    hasActiveFilters={hasActiveFilters}
                />
            </div>
        </div>
    );
}
